import { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL, getToken, photoUrl, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Video, Volume2, VolumeX, Send, Copy, Link2, Radio, Camera, Maximize2, Minimize2, X, ExternalLink } from "lucide-react";
import { Lightbox } from "@/components/Lightbox";

function supportedMime(m) {
  if (!window.MediaSource) return null;
  const list = [];
  if (m) list.push(m);
  if (m && m.startsWith("video/mp4")) list.push('video/mp4;codecs="avc1.42E01E,mp4a.40.2"', 'video/mp4;codecs="avc1.64001f,mp4a.40.2"');
  if (m && m.startsWith("video/webm")) list.push("video/webm;codecs=vp8,opus");
  for (const x of list) { try { if (MediaSource.isTypeSupported(x)) return x; } catch (_) {} }
  return null;
}

export function LiveViewer({ incidentId, onSaved }) {
  const videoRef = useRef(null);
  const imgRef = useRef(null);
  const wsRef = useRef(null);
  const msRef = useRef(null);
  const sbRef = useRef(null);
  const queueRef = useRef([]);
  const mimeRef = useRef(null);
  const modeRef = useRef("media");
  const lastUrlRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [enlarged, setEnlarged] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [session, setSession] = useState(null);
  const [online, setOnline] = useState(false);
  const [mode, setMode] = useState("media");
  const [muted, setMuted] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snaps, setSnaps] = useState([]);
  const [vehShares, setVehShares] = useState([]);
  const [snapLb, setSnapLb] = useState(null);
  const [recip, setRecip] = useState("contact");
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [winPos, setWinPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    api.get("/contacts").then(({ data }) => setContacts(data)).catch(() => {});
    api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
    return () => teardown(); // eslint-disable-line
  }, []);

  // Only stream while the window is open (keeps the incident page light & the notepad reachable)
  useEffect(() => {
    if (open && session && !wsRef.current) openViewer(session.token);
    if (open) api.get(`/incidents/${incidentId}/vehicle-shares`).then(({ data }) => setVehShares(data || [])).catch(() => {});
    if (!open) { teardown(); setEnlarged(false); }
  }, [open]); // eslint-disable-line

  const viewChannel = (tok) => { setSession({ token: tok, vehicle_channel: true }); openViewer(tok); };

  const teardown = () => {
    try { wsRef.current && wsRef.current.close(); } catch (_) {}
    try { if (msRef.current && msRef.current.readyState === "open") msRef.current.endOfStream(); } catch (_) {}
    try { lastUrlRef.current && URL.revokeObjectURL(lastUrlRef.current); } catch (_) {}
    wsRef.current = null; msRef.current = null; sbRef.current = null; queueRef.current = []; mimeRef.current = null; lastUrlRef.current = null;
    setOnline(false);
  };

  const flush = () => {
    const sb = sbRef.current;
    if (!sb || sb.updating || queueRef.current.length === 0) return;
    const chunk = queueRef.current.shift();
    try { sb.appendBuffer(chunk); }
    catch (e) {
      if (e && e.name === "QuotaExceededError") {
        try { const b = sb.buffered; if (b.length) sb.remove(b.start(0), Math.max(b.start(0), b.end(b.length - 1) - 8)); } catch (_) {}
        queueRef.current.unshift(chunk);
      }
    }
  };
  const onUpdateEnd = () => {
    const sb = sbRef.current;
    try {
      const b = sb.buffered;
      if (b.length && b.end(b.length - 1) - b.start(0) > 30 && !sb.updating) { sb.remove(b.start(0), b.end(b.length - 1) - 20); return; }
    } catch (_) {}
    flush();
  };
  const ensureSb = () => {
    const ms = msRef.current;
    if (!ms || ms.readyState !== "open" || sbRef.current || !mimeRef.current) return;
    const mime = supportedMime(mimeRef.current);
    if (!mime) return;
    try {
      const sb = ms.addSourceBuffer(mime);
      sb.mode = "sequence";
      sb.addEventListener("updateend", onUpdateEnd);
      sbRef.current = sb;
      flush();
    } catch (_) {}
  };
  const renderJpeg = (data) => {
    const img = imgRef.current; if (!img) return;
    const url = URL.createObjectURL(new Blob([data], { type: "image/jpeg" }));
    img.src = url;
    if (lastUrlRef.current) { try { URL.revokeObjectURL(lastUrlRef.current); } catch (_) {} }
    lastUrlRef.current = url;
  };

  const openViewer = (token) => {
    teardown();
    modeRef.current = "media"; setMode("media");
    const video = videoRef.current;
    if (!video) return;
    const ms = new MediaSource();
    msRef.current = ms;
    video.src = URL.createObjectURL(ms);
    ms.addEventListener("sourceopen", ensureSb);
    const wsBase = BACKEND_URL.replace(/^https/, "wss").replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/api/ws/live/${token}?role=viewer&auth=${getToken()}`);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    ws.onmessage = (e) => {
      if (typeof e.data === "string") {
        try {
          const obj = JSON.parse(e.data);
          if (obj.type === "broadcaster") setOnline(!!obj.online);
          if (obj.type === "mode") { modeRef.current = obj.mode; setMode(obj.mode); }
          if (obj.type === "mime") { mimeRef.current = obj.mime; ensureSb(); }
        } catch (_) {}
        return;
      }
      if (modeRef.current === "jpeg") { setOnline(true); renderJpeg(e.data); return; }
      queueRef.current.push(new Uint8Array(e.data));
      ensureSb(); flush();
      const v = videoRef.current;
      if (v && v.paused) v.play().catch(() => {});
    };
  };

  const onPickContact = (id) => {
    setContactId(id);
    const c = contacts.find((x) => x.id === id);
    if (c) { setName(c.name || ""); setEmail(c.email || ""); setPhone(c.phone || ""); }
  };

  const createSession = async (doSend) => {
    if (recip === "tablet" && !vehicleId) { toast.error("Kies een voertuig"); return; }
    setCreating(true);
    try {
      const { data } = await api.post(`/incidents/${incidentId}/live-sessions`, {
        recipient_name: name, email, phone,
        contact_id: recip === "contact" ? (contactId || null) : null,
        target_vehicle_id: recip === "tablet" ? vehicleId : null,
        audio: true, send_email: doSend,
      });
      setSession(data);
      openViewer(data.token);
      if (recip === "tablet") toast.success("Verzoek naar voertuigtablet gestuurd");
      else if (doSend) {
        if (data.email_sent) toast.success("Live-link per e-mail verstuurd");
        else toast.message("Sessie aangemaakt — geen e-mail verstuurd (vul een e-mailadres in). Deel de link handmatig.");
      } else { toast.success("Live-link aangemaakt"); }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const copyLink = () => { if (session?.link) navigator.clipboard.writeText(session.link).then(() => toast.success("Link gekopieerd")); };
  const toggleMute = () => { const v = videoRef.current; if (v) { v.muted = !v.muted; setMuted(v.muted); } };
  const startDrag = (e) => {
    const p = e.touches ? e.touches[0] : e;
    dragRef.current = { sx: p.clientX, sy: p.clientY, ox: winPos.x, oy: winPos.y };
    const move = (ev) => { const q = ev.touches ? ev.touches[0] : ev; setWinPos({ x: dragRef.current.ox + (q.clientX - dragRef.current.sx), y: dragRef.current.oy + (q.clientY - dragRef.current.sy) }); };
    const end = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", end); window.removeEventListener("touchmove", move); window.removeEventListener("touchend", end); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", end);
    window.addEventListener("touchmove", move, { passive: false }); window.addEventListener("touchend", end);
  };

  const saveSnapshot = () => {
    const isJpeg = modeRef.current === "jpeg";
    const src = isJpeg ? imgRef.current : videoRef.current;
    const w = isJpeg ? (src && src.naturalWidth) : (src && src.videoWidth);
    const h = isJpeg ? (src && src.naturalHeight) : (src && src.videoHeight);
    if (!src || !w || !h) { toast.error("Nog geen live beeld om vast te leggen"); return; }
    setSaving(true);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    try { canvas.getContext("2d").drawImage(src, 0, 0, w, h); } catch (e) { setSaving(false); toast.error("Snapshot mislukt"); return; }
    canvas.toBlob(async (blob) => {
      if (!blob) { setSaving(false); toast.error("Snapshot mislukt"); return; }
      const fd = new FormData();
      fd.append("file", blob, `live-snapshot-${Date.now()}.jpg`);
      try {
        const { data } = await api.post(`/incidents/${incidentId}/photos?source=live_snapshot`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setSnaps((prev) => [data, ...prev]);
        toast.success("Snapshot opgeslagen bij incident");
        onSaved && onSaved();
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
      finally { setSaving(false); }
    }, "image/jpeg", 0.85);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="live-viewer-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Radio className="h-4 w-4 text-red-600" /> Live beeld
          {session && <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${online ? "bg-red-600 text-white" : "bg-slate-100 text-slate-500"}`}>{online ? "LIVE" : "wacht…"}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="popout-live-btn" onClick={() => window.open(`/popout/live/${incidentId}`, `dispatchme-live-${incidentId}`, "width=1000,height=760,menubar=no,toolbar=no")} title="Open in apart venster"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            <ExternalLink className="h-3.5 w-3.5" /> Apart venster
          </button>
          <button data-testid="open-live-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500">
            <Send className="h-3.5 w-3.5" /> {session ? "Openen" : "Uitnodigen"}
          </button>
        </div>
      </div>
      <p className="mt-1 text-xs text-slate-400">Opent in een venster, zodat het kladblok vrij blijft.</p>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl" data-testid="live-invite-modal">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><Radio className="h-4 w-4 text-red-600" /> Live meekijken via mobiel/tablet</div>
              <button data-testid="live-close-btn" onClick={() => setOpen(false)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
              {/* Left — verstuur / deel */}
              <div className="space-y-3">
                {vehShares.length > 0 && (
                  <div data-testid="veh-shares" className="space-y-2 rounded-xl border border-blue-200 bg-blue-50 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">Voertuig deelt beeld</div>
                    {vehShares.map((s) => (
                      <button key={s.token} data-testid={`veh-share-${s.vehicle_id}`} onClick={() => viewChannel(s.token)} className="flex w-full items-center justify-between rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50">
                        <span className="font-mono">{s.callsign}</span><span className="text-xs text-blue-600">Bekijk</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Verstuur naar</div>
                <div className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1 text-xs font-semibold">
                  {[["contact", "Contact"], ["tablet", "Voertuigtablet"], ["handmatig", "E-mail/SMS"]].map(([k, l]) => (
                    <button key={k} data-testid={`recip-${k}`} onClick={() => setRecip(k)} className={`rounded-lg py-1.5 ${recip === k ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}>{l}</button>
                  ))}
                </div>
                {recip === "contact" && (
                  <select data-testid="live-contact-select" value={contactId} onChange={(e) => onPickContact(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500">
                    <option value="">— Kies contact —</option>
                    {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.role_label ? ` · ${c.role_label}` : ""}</option>)}
                  </select>
                )}
                {recip === "tablet" && (
                  <select data-testid="live-vehicle-select" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500">
                    <option value="">— Kies voertuig —</option>
                    {vehicles.map((v) => <option key={v.id} value={v.id}>{v.callsign}{v.name ? ` · ${v.name}` : ""}</option>)}
                  </select>
                )}
                {recip === "handmatig" && (
                  <div className="space-y-2">
                    <input data-testid="live-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Naam (optioneel)" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                    <div className="grid grid-cols-2 gap-2">
                      <input data-testid="live-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                      <input data-testid="live-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="06… (SMS binnenkort)" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {recip === "tablet" ? (
                    <button data-testid="live-tablet-btn" onClick={() => createSession(false)} disabled={creating || !vehicleId} className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-60"><Radio className="h-4 w-4" /> Stuur naar tablet</button>
                  ) : (
                    <>
                      <button data-testid="live-send-btn" onClick={() => createSession(true)} disabled={creating} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60"><Send className="h-4 w-4" /> Verstuur per e-mail</button>
                      <button data-testid="live-createlink-btn" onClick={() => createSession(false)} disabled={creating} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"><Link2 className="h-4 w-4" /> Alleen link</button>
                    </>
                  )}
                </div>
                {session?.link && (
                  <div data-testid="live-link-box" className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2.5">
                    <input readOnly value={session.link} className="min-w-0 flex-1 bg-transparent text-xs text-slate-600 outline-none" />
                    <button data-testid="live-copy-btn" onClick={copyLink} className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"><Copy className="h-3.5 w-3.5" /> Kopieer</button>
                  </div>
                )}
              </div>

              {/* Right — live beeld */}
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Live beeld</div>
                <div className={enlarged ? "fixed z-[1000] flex flex-col overflow-hidden rounded-xl border border-slate-300 bg-black shadow-2xl" : "relative"} style={enlarged ? { left: winPos.x, top: winPos.y, width: "52vw", height: "60vh" } : undefined} data-testid="live-video-wrap">
                  {enlarged && (
                    <div key="hdr" onMouseDown={startDrag} onTouchStart={startDrag} className="flex shrink-0 cursor-move select-none items-center justify-between bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white">
                      <span>Live beeld · versleep</span>
                      <button onClick={() => setEnlarged(false)} className="rounded p-1 hover:bg-white/10"><Minimize2 className="h-4 w-4" /></button>
                    </div>
                  )}
                  <div key="vid" className={enlarged ? "relative flex-1" : "relative aspect-video w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-900"}>
                      <video ref={videoRef} data-testid="live-viewer-video" playsInline autoPlay className="h-full w-full object-contain" style={{ display: mode === "jpeg" ? "none" : "block" }} />
                      <img ref={imgRef} alt="" data-testid="live-viewer-img" className="h-full w-full object-contain" style={{ display: mode === "jpeg" ? "block" : "none" }} />
                      <div className="absolute left-2 top-2 flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-bold" style={{ background: online ? "#dc2626" : "rgba(15,23,42,.7)", color: "#fff" }}>
                        {online ? <><span className="h-2 w-2 animate-pulse rounded-full bg-white" /> LIVE</> : <><Video className="h-3 w-3" /> {session ? "Wacht op camera…" : "Geen sessie"}</>}
                      </div>
                      <div className="absolute right-2 top-2 flex gap-1.5">
                        {session && mode !== "jpeg" && (
                          <button data-testid="live-mute-btn" onClick={toggleMute} className="rounded-lg bg-slate-900/70 p-2 text-white hover:bg-slate-900">{muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}</button>
                        )}
                        {session && (
                          <button data-testid="live-enlarge-btn" onClick={() => setEnlarged((v) => { const nv = !v; if (nv) setWinPos({ x: Math.round(window.innerWidth * 0.24), y: Math.round(window.innerHeight * 0.16) }); return nv; })} className="rounded-lg bg-slate-900/70 p-2 text-white hover:bg-slate-900">{enlarged ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}</button>
                        )}
                      </div>
                      {session && online && (
                        <button data-testid="live-snapshot-btn" onClick={saveSnapshot} disabled={saving} className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-lg bg-white/90 px-2.5 py-1.5 text-xs font-semibold text-slate-800 shadow hover:bg-white disabled:opacity-60"><Camera className="h-3.5 w-3.5" /> {saving ? "Opslaan…" : "Snapshot"}</button>
                      )}
                  </div>
                </div>
                {snaps.length > 0 && (
                  <div data-testid="live-snap-strip" className="flex gap-2 overflow-x-auto pt-1">
                    {snaps.map((p, idx) => (
                      <button key={p.id} data-testid={`live-snap-${p.id}`} onClick={() => setSnapLb(idx)} className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-slate-200">
                        <img src={photoUrl(p.id)} alt="" className="h-full w-full object-cover" />
                        <span className="absolute left-0.5 top-0.5 rounded bg-red-600 px-1 text-[7px] font-bold text-white">LIVE</span>
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-400">{session ? (online ? "Live beeld van de mobiele telefoon/tablet." : "Wachten tot de ontvanger de link opent…") : "Nog geen live-sessie."}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {snapLb !== null && <Lightbox photos={snaps} startIndex={snapLb} onClose={() => setSnapLb(null)} />}
    </div>
  );
}
